import http from 'services/http';

/** Partner portal API (student-backend src/providers). services/http resolves with the response body. */
const body = <T,>(p: Promise<unknown>) => p as Promise<T>;

export interface PartnerMe {
  org: { id: string; name: string; website: string | null; status: string; approved_updates: number };
  role: 'owner' | 'editor';
  publishes_directly: boolean;
  reviewed_updates_left: number;
  needs_you: { id: string; program_id: number; fields: string[]; message: string | null }[];
}

export interface PartnerProgramRow {
  id: number;
  gradmap_number: string;
  name: string;
  program_status: string | null;
  published: boolean;
  competitiveness_label: string | null;
  sessions: number;
  next_deadline: string | null;
  last_update_status: string | null;
}

export interface PartnerProgramView {
  program: Record<string, any>;
  sessions: Record<string, any>[];
  topics: { slug: string; name: string }[];
  pending_update: { id: string; status: string; changes: any; created_at: string } | null;
}

export interface SaveResult {
  update_id: string;
  program_id: number;
  status: 'pending' | 'published';
  message: string;
}

export interface StatRow {
  id: number;
  name: string;
  views_30d_app: number | null;
  views_30d_public: number | null;
  saves: number | null;
  applying: number | null;
}

export interface TeamRow {
  id: number;
  role: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export const partnerApi = {
  me: () => body<PartnerMe>(http.get('api/providers/me')),
  programs: () => body<PartnerProgramRow[]>(http.get('api/providers/programs')),
  program: (id: number) => body<PartnerProgramView>(http.get(`api/providers/programs/${id}`)),
  save: (id: number, data: unknown) => body<SaveResult>(http.put(`api/providers/programs/${id}`, data)),
  create: (data: unknown) => body<SaveResult>(http.post('api/providers/programs', data)),
  claim: (programId: number, note: string) => body<{ id: string; status: string; message: string }>(http.post('api/providers/claims', { program_id: programId, note })),
  requestTopic: (term: string, programId: number | null) => body<unknown>(http.post('api/providers/topic-requests', { term, program_id: programId })),
  reply: (id: string, text: string) => body<unknown>(http.post('api/providers/replies', { id, body: text })),
  stats: () => body<StatRow[]>(http.get('api/providers/stats')),
  team: () => body<TeamRow[]>(http.get('api/providers/team')),
  invite: (data: { email: string; first_name: string; last_name: string }) => body<unknown>(http.post('api/providers/team', data)),
  topics: () => body<{ key: string; label: string; topics: { slug: string; name: string }[] }[]>(http.get('api/programs/topics')),

  // Public (no sign-in)
  listings: (q: string) => body<{ id: number; name: string; host: string | null; managed: boolean }[]>(http.get('api/public/providers/listings', { params: { q } })),
  /** The programs in a personal invite link (?programs=PRG-0057,PRG-0058). */
  listingsByCodes: (codes: string) => body<{ id: number; name: string; host: string | null; managed: boolean }[]>(http.get('api/public/providers/listings', { params: { codes } })),
  apply: (data: { org_name: string; website?: string; name: string; title?: string; email: string; phone?: string; message?: string; program_ids?: number[]; news_opt_in?: boolean }) =>
    body<{ id: string; message?: string }>(http.post('api/public/providers/apply', data)),
};
